import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import { useRouter } from 'next/router'
import UserLoaded from '../advisor/UserLoaded'
import { Waypoint } from 'react-waypoint'
import { AiOutlineClockCircle, AiOutlineDollar } from 'react-icons/ai'
import { BsGear } from 'react-icons/bs'
import { BiArrowBack } from 'react-icons/bi'
import { FaChevronCircleRight } from 'react-icons/fa'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { inject, observer } from 'mobx-react'
import styled, { css } from 'styled-components'
import useSWR from 'swr'

import { PAGEURL, APIURL } from '/constants'
import {
  Button as ChaliceButton,
  Link as ChaliceLink
} from '/components/theme/button/Button'
import services from '/components/services'
import MediaCarousel from './MediaCarousel'
import RequestConsultationButton from './enroll-request/RequestConsultationButton'
import { openInNewTab, personalizeUrl } from '../utils'
import ChangeSection from './Tabs'
import RelatedProducts from './RelatedProducts'
import reviewStars from './starRatingFunction'
import DefaultLayout from '../layouts/DefaultLayout'
import styles from './Marketplace.module.scss'
import useModal from '/hooks/useModal'
import { Modal } from '/components/admin/Modal'
import { Sharing } from './ProductActions'

import { MagicForm } from '/components/auth/MagicFlow'
import { LazyForm } from '/components/auth/LazyFlow'
import { VisbilityContainer, HeaderPose, DivPose } from '../home/Animations'
import { ProductSection } from './ProductSection'
import { radial, Container } from '../theme/default/base'
import CouponCode from './enroll-request/CouponCode'
import MarketoForm from './enroll-request/MarketoForm'
import HubspotForm from './enroll-request/HubspotForm'

const UpgradeButton = ({ sopt, siteData }) => (
  <CtaWrapper id="upgrade" key="upgrade">
    <Link
      passHref
      href={siteData.get('upgrade_url') || `/subscribe/?sopt=${sopt || ''}`}
    >
      <ChaliceLink>
        {siteData.get('upgrade_text') || 'Upgrade to Access!'}
      </ChaliceLink>
    </Link>
  </CtaWrapper>
)

UpgradeButton.propTypes = {
  plan: PropTypes.number,
  siteData: PropTypes.object
}

const MagicModal = ({ siteData, product, onComplete }) => {
  const { visible, toggle } = useModal()
  const action_text =
    siteData.get('signup_text') || product.register_text || 'Start Saving Today'
  return (
    <div>
      <CtaWrapper id="signup-magic" key="signup-magic">
        <ChaliceButton onClick={toggle}>{action_text}</ChaliceButton>
      </CtaWrapper>
      <Modal
        title="Login or Join Now"
        visible={visible}
        toggle={toggle}
        maxWidth="400px"
      >
        <PaddedForm>
          <MagicForm onComplete={() => onComplete()} />
        </PaddedForm>
      </Modal>
    </div>
  )
}

const LazyAuthModal = ({ siteData, product, userData, ctas }) => {
  const [selected, setSelected] = useState(null)
  const router = useRouter()

  const onComplete = () => {
    if (selected.action_type === 'enroll-external') {
      openInNewTab(PAGEURL.PRODUCT_DETAILS_REDIRECT(product.slug, selected.id))
    } else if (selected.action_type === 'redirect') {
      const data = userData
      data['slug'] = product.slug
      openInNewTab(personalizeUrl(selected.action_url, data))
    } else if (
      selected.action_type === 'enroll' ||
      selected.action_type === 'enroll-calendar'
    ) {
      router.push(`${PAGEURL.PRODUCT_DETAILS}${product.slug}/consultation/`)
    }
    return null
  }
  return (
    <div>
      <Modal
        title={`Unlock ${siteData.get('name')} Savings`}
        visible={selected !== null}
        toggle={() => setSelected(null)}
        maxWidth="400px"
      >
        <PaddedForm>
          <LazyForm onComplete={() => onComplete()} />
        </PaddedForm>
      </Modal>
      {(ctas || []).map((cta) => (
        <CtaWrapper key={cta.id} id={cta.id}>
          <div>
            <ChaliceButton
              onClick={() => {
                setSelected(cta)
              }}
            >
              {cta.action_text}
            </ChaliceButton>
          </div>
        </CtaWrapper>
      ))}
    </div>
  )
}

const ProductDetailsDisplay = inject('store')(
  observer((props) => {
    const [ignore, setIgnore] = useState(null)
    const [active, setActive] = useState('description')
    const [button_nav, setButtonNav] = useState(false)
    const [sections_list, setSectionsList] = useState([])
    const [showModal, setShowModal] = useState(false)
    const reviewSectionRef = React.createRef()
    const faqSectionRef = React.createRef()
    const store = props.store
    const router = useRouter()

    const product = props.product

    const {
      data: ctaData,
      error: ctaError,
      mutate: ctaMutate
    } = useSWR(APIURL.API_MARKETPLACE_CTA(product.slug), services.get_v2)
    const ctas = ctaData?.response?.ctas
    const ctasLoading = !ctaData && !ctaError
    const needsUpgrade = ctaData?.response?.needs_upgrade

    function recordPageView() {
      if (product) {
        if (!store.isAuthenticated()) {
          try {
            if (typeof window !== 'undefined') {
              const raw = window.localStorage.getItem('views') || '[]'
              const views = JSON.parse(raw)
              views.push({
                product_id: product.id,
                when: dayjs().toISOString()
              })
              window.localStorage.setItem('views', JSON.stringify(views))
            }
          } catch (e) {
            console.error('Unable to record view')
            console.error(e)
          }
        } else {
          store.pageview([
            {
              product_id: product.id,
              when: dayjs().toISOString()
            }
          ])
        }
      }
    }

    useEffect(() => {
      setTimeout(recordPageView, 1000)
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0)
        let scrollTimeout
        window.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout)
          scrollTimeout = setTimeout(() => {
            setIgnore(false)
          }, 100)
        })
      }
      return () => {
        store.updateNavbar('')
      }
    }, [])

    async function setSectionList() {
      const sections_list = []
      sections_list.push('description')
      if (product.chalice_advantage) {
        sections_list.push('advantage')
      }
      if (product.benefits) {
        sections_list.push('benefits')
      }
      if (product?.reviews?.length > 0) {
        sections_list.push('reviews')
      }
      if (product.faqs?.length > 0) {
        sections_list.push('faq')
      }
      setSectionsList(sections_list)
      return sections_list
    }

    function signupUrl() {
      if (store.siteData.get('signup_url')) {
        return store.siteData.get('signup_url')
      } else {
        const next = `${PAGEURL.PRODUCT_DETAILS}${product.slug}/consultation/`
        return `${PAGEURL.SIGNUP}?next=${encodeURIComponent(next)}`
      }
    }

    function buildCtas(userData, disabled, display, location, classes) {
      const ctaComps = []
      let disabled_for = ''
      if (disabled) {
        disabled_for = 'organization'
      }
      if (ctas) {
        ctas.forEach((cta) => {
          if (
            cta.action_type === 'enroll' ||
            cta.action_type === 'enroll-calendar'
          ) {
            const action_text = cta.action_text || 'Request Consultation'
            ctaComps.push(
              <CtaWrapper key={cta.id} id={cta.id}>
                <RequestConsultationButton
                  allowAutoLaunch={true}
                  product_id={product ? product.id : null}
                  product_slug={product ? product.slug : null}
                  enrollment_type={product ? product.enrollment_type : 'none'}
                  company_id={company ? company.id : null}
                  action_text={action_text}
                  disabled={disabled}
                  disabled_for={disabled_for}
                  product={product}
                  email={cta.email}
                  link={cta.calendar_link}
                />
              </CtaWrapper>
            )
          } else if (cta.action_type === 'enroll-external') {
            const action_text = cta.action_text || 'Request Consultation'
            ctaComps.push(
              <CtaWrapper key={cta.id} id={cta.id}>
                <a
                  href={PAGEURL.PRODUCT_DETAILS_REDIRECT(product.slug, cta.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ChaliceButton
                    disabled={disabled}
                    className={classNames(classes)}
                  >
                    {action_text}&nbsp;
                    <FaChevronCircleRight />
                  </ChaliceButton>
                </a>
              </CtaWrapper>
            )
          } else if (cta.action_type === 'redirect') {
            const action_text = cta.action_text || 'Request Consultation'
            const clazz = classes
            const data = userData
            data['slug'] = product.slug
            if (cta.action_url[0] === '/') {
              ctaComps.push(
                <CtaWrapper key={cta.id} id={cta.id}>
                  <a
                    href={personalizeUrl(cta.action_url, data)}
                    target="_blank"
                    disabled={disabled}
                    rel="noopener noreferrer"
                    passHref
                  >
                    <ChaliceButton className={classNames(clazz)}>
                      {action_text}
                    </ChaliceButton>
                  </a>
                </CtaWrapper>
              )
            } else {
              ctaComps.push(
                <CtaWrapper key={cta.id} id={cta.id}>
                  <a
                    target="_blank"
                    disabled={disabled}
                    rel="noopener noreferrer"
                    href={personalizeUrl(cta.action_url, userData)}
                  >
                    <ChaliceButton className={classNames(clazz)}>
                      {action_text}
                    </ChaliceButton>
                  </a>
                </CtaWrapper>
              )
            }
          } else if (cta.action_type === 'comingsoon') {
            ctaComps.push(
              <CtaWrapper id="comingsoon">
                <ComingSoon>Coming Soon</ComingSoon>
              </CtaWrapper>
            )
          } else if (cta.action_type === 'coupon-code') {
            const toggleModal = () => {
              setShowModal(showModal === cta ? null : cta)
            }
            ctaComps.push(
              <CtaWrapper key={cta.id} id={cta.id}>
                <div>
                  <ChaliceButton
                    onClick={() => {
                      toggleModal()
                      window.scrollTo(0, 0)
                    }}
                  >
                    {cta.action_text}
                  </ChaliceButton>
                  {showModal && display === 'none' && location !== 'bar' && (
                    <Modal
                      title={cta.title}
                      includeTopBar={false}
                      visible={showModal === cta}
                      toggle={toggleModal}
                      maxWidth="400px"
                    >
                      <CouponCode
                        cta={cta}
                        product={product}
                        close={() => setShowModal(null)}
                      />
                    </Modal>
                  )}
                </div>
              </CtaWrapper>
            )
          } else if (cta.action_type === 'marketo_form') {
            ctaComps.push(
              <CtaWrapper key={cta.id} id={cta.id}>
                <div>
                  {(!showModal || display !== 'none') && (
                    <ChaliceButton
                      onClick={() => {
                        setShowModal(!showModal)
                        window.scrollTo(0, 0)
                      }}
                    >
                      {cta.action_text}
                    </ChaliceButton>
                  )}
                  {showModal && display === 'none' && location !== 'bar' && (
                    <MarketoForm cta={cta} product={product} />
                  )}
                </div>
              </CtaWrapper>
            )
          } else if (cta.action_type === 'hubspot_form') {
            ctaComps.push(
              <CtaWrapper key={cta.id} id={cta.id}>
                <div>
                  <ChaliceButton
                    onClick={() => {
                      setShowModal(!showModal)
                      window.scrollTo(0, 0)
                    }}
                  >
                    {cta.action_text}
                  </ChaliceButton>
                  {showModal && display === 'none' && location !== 'bar' && (
                    <HubspotForm
                      cta={cta}
                      product={product}
                      toggle={() => setShowModal(!showModal)}
                    />
                  )}
                </div>
              </CtaWrapper>
            )
          }
        })
      }
      return ctaComps
    }

    function renderEnrollRequest(company, display, location, classes) {
      const userData = store.sessionData.userData
      const disabled = !userData.can_request_consultation
      if (!product || !company) {
        return null
      }

      if (
        !store.isAuthenticated() &&
        store.siteData.get('identity_provider') == 'local_lazy'
      ) {
        return (
          <LazyAuthModal
            siteData={store.siteData}
            product={product}
            userData={userData}
            ctas={ctas}
          />
        )
      } else if (
        !store.isAuthenticated() &&
        store.siteData.get('identity_provider') == 'local_auto'
      ) {
        return buildCtas(userData, false, display, location, classes)
      } else if (
        !store.isAuthenticated() &&
        !store.siteData.get('allow_signup')
      ) {
        //
      } else if (!store.isAuthenticated()) {
        return renderRegButton()
      } else if (needsUpgrade) {
        return renderUpgradeButton()
      } else {
        return buildCtas(userData, disabled, display, location, classes)
      }
    }

    function renderOfferedBy() {
      let companyName = company?.name
      if (company?.website) {
        companyName = (
          <span className={styles.name}>
            <a href={company.website} rel="noopener noreferrer" target="_blank">
              {company.name}
            </a>
          </span>
        )
      }
      return <div>Offered by&nbsp;{companyName}</div>
    }

    function scrollTo(id) {
      if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        const headerHeight =
          document.getElementById('chalice-header').clientHeight
        const navHeight = document.getElementById('product-nav').clientHeight
        window.scrollTo({
          left: 0,
          top: document.getElementById(id).offsetTop - headerHeight - navHeight,
          behavior: 'smooth'
        })
      }
      setActive(id)
    }

    function onEnter(obj, activeClass) {
      if (!ignore && obj.previousPosition === Waypoint.above) {
        setActive(activeClass)
      }
    }

    function onLeave(obj, nextActiveClass) {
      if (!ignore && obj.currentPosition === Waypoint.above) {
        setActive(nextActiveClass)
      }
    }

    function onEnterTop(obj) {
      setButtonNav(false)
    }

    function onLeaveTop(obj) {
      setButtonNav(true)
    }

    if (!product) {
      router.push('/404')
    }
    const company = product.partner
    const renderRegButton = () => {
      if (!store.isAuthenticated() && !store.siteData.get('allow_signup')) {
        return null
      }
      if (store.sessionData.userData.id === undefined) {
        if (store.siteData.get('identity_provider') === 'magic_link') {
          return (
            <MagicModal
              product={product}
              siteData={store.siteData}
              onComplete={() => ctaMutate()}
            />
          )
        } else {
          const action_text =
            store.siteData.get('signup_text') ||
            product.register_text ||
            'Start Saving Today'
          return (
            <CtaWrapper id="signup" key="signup">
              <Link href={signupUrl()} passHref>
                <ChaliceButton>{action_text}</ChaliceButton>
              </Link>
            </CtaWrapper>
          )
        }
      }
      return null
    }

    const renderUpgradeButton = () => {
      if (!store.isAuthenticated()) {
        return null
      }
      let sopt = null
      if ((product.plans || []).length > 0) {
        const p = product.plans[0]
        if (p.prices && p.prices.length > 0) {
          sopt = p.prices[0].stripe_price_id
        }
      }
      return <UpgradeButton siteData={store.siteData} sopt={sopt} />
    }

    const SECTIONS = [
      {
        key: 'description',
        title: product.tagline,
        description: product.product_description,
        imageUrl: product?.description_media?.media_website
      },
      {
        key: 'advantage',
        title: '{{ name }} Advantage',
        description: product.chalice_advantage,
        imageUrl: product?.advantage_media?.media_website
      },
      {
        key: 'benefits',
        title: 'Benefits',
        description: product.benefits,
        imageUrl: product?.benefit_media?.media_website
      }
    ]
      .filter((s) => s.description !== null && s.description !== '')
      .map((s, i) => ({
        ...s,
        cta: false,
        // imageUrl: 'https://static.mywebsites360.com/ad8fd96c0fd04a78a79dfb3ff599a9ad/i/acfd3c1205c34d988893921d1acc0cbf/2/4SoifmQp45JMgBnHjfgmz/homepage-image.png?dpr=2',
        reverse: i % 2 === 0,
        bgColor: i % 2 === 0 ? '#fafafa' : '#fff'
        // sideColor: '#fafafa'
      }))
    const hasImages =
      SECTIONS.filter((s) => (s.imageUrl || '').trim().length > 0).length > 0

    return (
      <Product id="product" isEnterprise={store.isEnterprise()}>
        {!props.explore && (
          <ProductTopNav display={button_nav ? 'flex' : 'none'}>
            <Container>
              <NavGrid>
                <NavHeader>{product.product_name}</NavHeader>
                <NavActions>
                  <CtaRow>
                    <UserLoaded>
                      <div style={{ display: 'flex' }}>
                        {renderEnrollRequest(
                          company,
                          button_nav ? 'flex' : 'none',
                          'bar',
                          []
                        )}
                      </div>
                    </UserLoaded>
                  </CtaRow>
                </NavActions>
              </NavGrid>
            </Container>
          </ProductTopNav>
        )}
        <Waypoint
          key={'product-block'}
          onEnter={(obj) => {
            onEnterTop(obj)
          }}
          onLeave={(obj) => {
            onLeaveTop(obj)
          }}
        >
          <ProductBlock id="product-block">
            <VisbilityContainer>
              <ProductBackground>
                {product?.hero_media?.media_website && (
                  <ProductBackgroundImg
                    src={product?.hero_media?.media_website}
                  />
                )}
              </ProductBackground>
              <Container>
                <ProductTitle
                  hasImages={product?.hero_media?.media_website || hasImages}
                >
                  <Link href={PAGEURL.MARKETPLACE} passHref>
                    <BackLink>
                      <BiArrowBack /> Back to{' '}
                      {store.siteData.get('exchange_link_text') ||
                        'Marketplace'}
                    </BackLink>
                  </Link>
                  <Sharing
                    siteData={store.siteData}
                    isLoggedIn={store.isUserLoggedIn()}
                    product={product}
                    productUserData={props.productUserData}
                    registerCallback={() => {
                      window.location = signupUrl()
                    }}
                  />
                  <H1>{product.product_name}</H1>
                  <div className={styles.title_partner_name}>
                    {renderOfferedBy()}
                    <div className={styles.star_color}>
                      {store.product_details.review_count > 0 && (
                        <React.Fragment>
                          {reviewStars(store.product_details.avg_stars)}
                          <a onClick={() => scrollTo('reviews')}>
                            {' '}
                            {store.product_details.review_count} member reviews{' '}
                          </a>
                        </React.Fragment>
                      )}
                      {store.product_details.review_count > 0 &&
                        store.product_details.faq_count > 0 && (
                          <span>|&nbsp;</span>
                        )}
                      {store.product_details.faq_count > 0 && (
                        <a onClick={() => scrollTo('faq')}>
                          {store.product_details.faq_count} answered questions
                        </a>
                      )}
                    </div>
                  </div>
                  <Stats>
                    {product.approval_process && (
                      <StatsContainer>
                        <StatsIcon>
                          <AiOutlineClockCircle />
                        </StatsIcon>
                        <div className={styles.stat_blocks}>
                          <div>Estimated Approval Process</div>
                          <StatsText>{product.approval_process}</StatsText>
                        </div>
                      </StatsContainer>
                    )}
                    {product.integration_time && (
                      <StatsContainer>
                        <StatsIcon>
                          <BsGear />
                        </StatsIcon>
                        <div className={styles.stat_blocks}>
                          <span>Integration Time</span>
                          <StatsText>{product.integration_time}</StatsText>
                        </div>
                      </StatsContainer>
                    )}
                    {product.deal && (
                      <StatsContainer>
                        <StatsIcon>
                          <AiOutlineDollar />
                        </StatsIcon>
                        <div className={styles.stat_blocks}>
                          <span>Savings</span>
                          <StatsText>{product.deal}</StatsText>
                        </div>
                      </StatsContainer>
                    )}
                  </Stats>
                  <CtaRow>
                    <UserLoaded>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {renderEnrollRequest(
                          company,
                          button_nav ? 'flex' : 'none',
                          'body',
                          []
                        )}
                      </div>
                    </UserLoaded>
                  </CtaRow>
                </ProductTitle>
              </Container>
            </VisbilityContainer>
          </ProductBlock>
        </Waypoint>
        {product.custom_html ? (
          <DangerousElement markup={product.custom_html} />
        ) : (
          <>
            <ProductNav explore={props.explore} id="product-nav">
              <Container>
                <ProductNavList hasImages={hasImages}>
                  <ProductNavLi
                    key="description"
                    active={active === 'description'}
                  >
                    <div
                      onClick={(e) => {
                        setIgnore(true)
                        if (!props.explore) {
                          scrollTo('description')
                        }
                      }}
                    >
                      Description
                    </div>
                  </ProductNavLi>
                  {product.chalice_advantage && (
                    <ProductNavLi
                      key="advantage"
                      active={active === 'advantage'}
                    >
                      <div
                        onClick={(e) => {
                          setIgnore(true)
                          if (!props.explore) {
                            scrollTo('advantage')
                          }
                        }}
                      >
                        Advantage
                      </div>
                    </ProductNavLi>
                  )}
                  {product.benefits && (
                    <ProductNavLi key="benefits" active={active === 'benefits'}>
                      <div
                        onClick={(e) => {
                          setIgnore(true)
                          if (!props.explore) {
                            scrollTo('benefits')
                          }
                        }}
                      >
                        Benefits
                      </div>
                    </ProductNavLi>
                  )}
                  {store.product_details.review_count > 0 && (
                    <ProductNavLi key="reviews" active={active === 'reviews'}>
                      <div
                        onClick={(e) => {
                          setIgnore(true)
                          if (!props.explore) {
                            scrollTo('reviews')
                          }
                        }}
                      >
                        Reviews
                      </div>
                    </ProductNavLi>
                  )}
                  {store.product_details.faq_count > 0 && (
                    <ProductNavLi key="faq" active={active === 'faq'}>
                      <div
                        onClick={(e) => {
                          setIgnore(true)
                          if (!props.explore) {
                            scrollTo('faq')
                          }
                        }}
                      >
                        FAQS
                      </div>
                    </ProductNavLi>
                  )}
                </ProductNavList>
              </Container>
            </ProductNav>
            <div>
              {SECTIONS.map((section) => (
                <Waypoint
                  key={section.imageUrl || section.key}
                  topOffset="173px"
                  onEnter={(obj) => {
                    onEnter(obj, section.key)
                  }}
                  onLeave={(obj) => {
                    onLeave(
                      obj,
                      sections_list[sections_list.indexOf(section.key) + 1] ||
                        null
                    )
                  }}
                >
                  <div id={section.key}>
                    <ProductSection
                      id={section.key}
                      title={section.title}
                      description={section.description}
                      cta={section.cta}
                      imageUrl={section.imageUrl}
                      reverse={section.reverse}
                      bgColor={section.bgColor}
                      sideColor={section.sideColor}
                    />
                  </div>
                </Waypoint>
              ))}
              <MediaCarousel product_id={product.id} />
              {product && (
                <ChangeSection
                  setSectionList={setSectionList}
                  onEnter={onEnter}
                  onLeave={onLeave}
                  product={product}
                  references={{ review: reviewSectionRef, faq: faqSectionRef }}
                />
              )}
              {!props.explore && (
                <Waypoint key="related-block">
                  <Container>
                    <RelatedProducts slug={product.slug} />
                  </Container>
                </Waypoint>
              )}
            </div>
          </>
        )}
      </Product>
    )
  })
)

ProductDetailsDisplay.propTypes = {
  product: PropTypes.object,
  store: PropTypes.object,
  autoLaunch: PropTypes.bool,
  ctas: PropTypes.array,
  needsUpgrade: PropTypes.bool
}

ProductDetailsDisplay.Layout = DefaultLayout

export default ProductDetailsDisplay

const PaddedForm = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ProductBlock = styled.div`
  position: relative;
  padding: 50px 0;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${(props) => props.theme.media.tablet`
    min-height: auto;
  `}
`

const ProductBackground = styled(DivPose)`
  /* background: ${(props) =>
    radial(props.theme?.colors?.primary || '#022241')}; */
  top: 0;
  right: 0;
  bottom: 0;
  left: 50%;
  opacity: ${(props) => props.opacity};
  z-index: 10;
  overflow: hidden;
  position: absolute;

  ${(props) => props.theme.media.tablet`
    display: none;
  `}
`

const ProductBackgroundImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const ProductTitle = styled.div`
  width: 50%;
  z-index: 11;
  padding-right: 20px;

  ${(props) =>
    !props.hasImages &&
    css`
      width: 60%;
      margin: 0 auto;
    `}

  ${(props) => props.theme.media.tablet`
    width: 100%;
  `}
`

const H1 = styled(HeaderPose)`
  font-size: 2rem;
`

const Stats = styled.div`
  margin-bottom: 20px;
  color: white;
  font-size: 12px;
  display: flex;
  flex-direction: row;
  flex-flow: wrap;
  align-items: center;
`

const StatsContainer = styled.div`
  padding: 10px 0;
  width: 100%;
  display: flex;
  align-items: center;
  color: #000;
`

const StatsIcon = styled.div`
  display: inline-block;
  width: 28px !important;
  height: 28px !important;
  margin-right: 10px;

  svg {
    font-size: 28px;
    width: 28px !important;
    height: 28px !important;
  }
`

const StatsText = styled.div`
  line-height: 18px;
  font-size: 18px;
`

const ProductTopNav = styled.div`
  position: fixed;
  z-index: 1001;
  height: 80px;
  height: var(--chalice-navbar-height);
  width: 100%;
  top: 0;
  /* background-color: ${(props) => props.theme.colors.secondary}; */
  background-color: #fff;
  display: flex;
  display: ${(props) => props.display};
  flex-direction: row;
  align-items: center;
`

const NavHeader = styled.h2`
  font-size: 1.5rem !important;
  overflow: hidden;
  flex: 1 1 auto;

  ${(props) => props.theme.media.phone`
    font-size: 0.9rem !important;
    display: none;
  `}
`

const CtaRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-flow: wrap;
`

const CtaWrapper = styled.div`
  margin: 0 5px 5px 0;
`

const NavActions = styled.div`
  display: flex;
  flex: 0 0 auto;

  ${(props) => props.theme.media.phone`
    overflow: auto;
    gap: 10px;
  `}
`

const NavGrid = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const ComingSoon = styled.div`
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 10px 20px;
`

const Product = styled.div`
  .cwp_custom_panel_main .deal {
    margin-bottom: 0;
    padding: 10px 0;
  }

  .integration_connect {
    padding: 10px 0;
  }

  .consultation_row {
    padding: 0 0 10px 0;
  }

  .benefits_main {
    margin-bottom: 30px;
  }

  .tag_line {
    font-weight: 600 !important;
    font-size: 1.1rem;
    margin: 0 0 20px 0;
  }

  .cwp_review_faq_main {
    h4 {
      font-weight: bold;
    }
    .product_faq {
      padding: 10px 0;
      /* border-bottom: $theme-light-grey solid 1px;*/
    }

    .cwp_faq_tab_main {
      margin-top: 6%;
    }

    .product_review {
      .review_tagline {
        font-weight: 600;
        font-size: 1.1rem;
        padding-left: 5px;
      }

      padding: 10px 0 20px 0;

      img {
        margin-bottom: 10px;
      }

      h5 {
        margin: 0 0 5px;
        color: $theme-black;
        font-size: 1.2rem;
      }

      .member_since,
      .member_stars {
        margin-bottom: 5px;

        & > span:first-child,
        & > span:first-child {
          color: $theme-black;
          font-weight: 500;
        }
      }

      .cwp_review_stars span:first-child {
        font-weight: 600;
      }

      .cwp_review_comment {
        font-size: 14px !important;
        margin-top: 10px;
      }

      .checked {
        color: #edab1e;
      }
    }

    .cwp_review_faq_card_scroll {
      max-height: 430px;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .profile_category_title {
      padding: 15px 0;
    }

    .cwp_faq_inner_main .panel {
      min-height: initial;
    }

    .cwp_review_inner_main {
      .cwp_review_inner_card {
      }

      .cwp_review_inner_card:not(:last-child) {
        margin-bottom: 20px;
      }
    }
  }

  @media (max-width: 400px) {
    .cwp_review_inner_card .cwp_review_stars > span:last-child {
      display: block;
    }
  }
  padding-top: var(--chalice-navbar-height);
`

const ProductNav = styled.div`
  position: ${(props) => !props.explore && 'sticky'};
  top: 80px;
  top: var(--chalice-navbar-height);
  background-color: white;
  z-index: 17;
  border-bottom: 1px solid #e4e4e4;
  padding: 15px;

  ${(props) => props.theme.media.phone`
    font-size: .7rem !important;
  `}
`

const ProductNavList = styled.ul`
  list-style-type: none;
  overflow-x: auto;
  white-space: nowrap;
  padding: 0;
  margin: 0;

  ${(props) =>
    !props.hasImages &&
    css`
      width: 60%;
      margin: 0 auto;
    `}

  ${(props) => props.theme.media.phone`
    display: flex;
    width: 100%;
    justify-content: space-between;
  `}
`

const ProductNavLi = styled.li`
  & > div {
    line-height: 42px;
  }

  ${(props) =>
    props.active &&
    css`
      font-weight: 800;
      color: ${(props) => props.theme.colors.primary};
    `}

  &.product_nav_btn {
    display: none !important;
  }
  font-weight: 500;
  cursor: pointer;
  display: inline-block;
  padding-right: 1.5rem !important;

  ${(props) => props.theme.media.phone`
    padding-right: 0 !important;
  `}
`

const BackLink = styled.a`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`

function DangerousElement({ markup }) {
  const elRef = useRef()

  useLayoutEffect(() => {
    const range = document.createRange()
    range.selectNode(elRef.current)
    const documentFragment = range.createContextualFragment(markup)

    // Inject the markup, triggering a re-run!
    elRef.current.innerHTML = ''
    elRef.current.append(documentFragment)
  }, [])

  return <div ref={elRef} dangerouslySetInnerHTML={{ __html: markup }} />
}
